<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import { formatBRL } from 'dashboard/helper/lais/currency';
import { moneyCents } from '../composables/salesCommissionForm';
import SalesCommissionPayerControls from './SalesCommissionPayerControls.vue';
import SalesCommissionPaymentPeriod from './SalesCommissionPaymentPeriod.vue';

const props = defineProps({
  disabledPaymentTriggers: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) },
  index: { type: Number, required: true },
  installment: { type: Object, required: true },
  installmentCount: { type: Number, default: 1 },
  netAmount: { type: [Number, String], default: '' },
  payerOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['update', 'updatePayer', 'updatePayerCount']);
const { t } = useI18n();
const installmentLabel = computed(() =>
  props.installmentCount === 1
    ? t('SALES.COMMISSION.PAYMENT.UNIQUE_INSTALLMENT')
    : t('SALES.COMMISSION.PAYMENT.ORDINAL_INSTALLMENT', {
        number: props.index + 1,
      })
);
const formattedNetAmount = computed(() =>
  formatBRL(moneyCents(props.netAmount) / 100, { fractionDigits: 2 })
);
</script>

<template>
  <LaisCard class="bg-lais-bg-soft">
    <template #header>
      <div
        class="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-test="commission-installment-header"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="rounded-full bg-lais-success/10 px-2.5 py-1 text-xs font-semibold text-lais-success"
            data-test="commission-installment-label"
          >
            {{
              installmentCount === 1
                ? t('SALES.COMMISSION.PAYMENT.INTEGRAL')
                : t('SALES.COMMISSION.PAYMENT.INSTALLMENT', {
                    number: index + 1,
                  })
            }}
          </span>
          <span class="text-xs font-semibold text-lais-text-muted">
            {{ t('SALES.COMMISSION.PAYMENT.COMMISSION') }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:justify-end">
          <span class="text-xs font-semibold text-lais-text-muted">
            {{ t('SALES.COMMISSION.PAYMENT.NET_COMMISSION') }}
          </span>
          <span
            class="rounded-full bg-lais-success/10 px-2.5 py-1 text-xs font-semibold text-lais-success"
            data-test="commission-installment-net-amount"
          >
            {{ formattedNetAmount }}
          </span>
        </div>
      </div>
    </template>
    <div class="grid gap-4 md:grid-cols-2">
      <LaisField :label="installmentLabel" :error="errors.amount" required>
        <input
          :value="installment.amount"
          inputmode="decimal"
          data-test="commission-installment-amount"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @input="emit('update', 'amount', $event.target.value)"
        />
      </LaisField>
      <SalesCommissionPaymentPeriod
        :disabled-periods="disabledPaymentTriggers"
        :error="errors.payment_trigger"
        :index="index"
        :value="installment.payment_trigger"
        @update="value => emit('update', 'payment_trigger', value)"
      />
    </div>
    <SalesCommissionPayerControls
      :errors="errors"
      :installment="installment"
      :payer-options="payerOptions"
      @update-payer="(...args) => emit('updatePayer', ...args)"
      @update-payer-count="count => emit('updatePayerCount', count)"
    />
  </LaisCard>
</template>
